import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Search, Store, Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog che fa cercare un negozio al cliente (Google Places via /stores/discover),
 * poi al click su un risultato chiama onPick(business) per aprire l'InviteSheet
 * che si occupa dell'auto-lookup email e dell'invio dell'invito.
 */
export default function InviteStoreSearchDialog({ open, onOpenChange, onPick, position }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setTouched(false);
      return;
    }
    const t = setTimeout(() => { try { inputRef.current?.focus(); } catch (_) {} }, 150);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const params = { q: term };
        if (position && position.lat && position.lng) {
          params.lat = position.lat;
          params.lng = position.lng;
        }
        const r = await api.get("/stores/discover", { params });
        setResults((r.data && r.data.stores) || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
        setTouched(true);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, position]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden" data-testid="invite-store-search-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="text-lg font-extrabold flex items-center gap-2 text-slate-900">
            <Store className="h-5 w-5 text-[#E63946]" /> Invita un negozio
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">Cerca il negozio che vuoi su KoupoNow. Troveremo noi l'email di contatto.</p>
        </DialogHeader>
        <div className="px-5 pt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome del negozio…"
              className="h-12 rounded-xl pl-10"
              data-testid="invite-store-search-input"
            />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
          </div>
        </div>
        <div className="px-5 pb-5 pt-3 max-h-[55vh] overflow-y-auto">
          {q.trim().length < 2 && (
            <p className="text-sm text-slate-400 text-center py-6">Scrivi almeno 2 lettere per iniziare la ricerca.</p>
          )}
          {q.trim().length >= 2 && !loading && results.length === 0 && touched && (
            <p className="text-sm text-slate-400 text-center py-6">Nessun negozio trovato. Prova con un altro nome.</p>
          )}
          <div className="space-y-1.5">
            {results.map((s) => (
              <button
                key={s.place_id}
                type="button"
                data-testid={`invite-pick-${s.place_id}`}
                onClick={() => {
                  if (s.on_platform) {
                    toast.success(`${s.name} è già su KoupoNow! 🎉`, { description: "Lo trovi tra le offerte: non serve invitarlo." });
                    return;
                  }
                  onPick && onPick(s);
                  onOpenChange(false);
                }}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${s.on_platform ? "bg-emerald-50/60 cursor-default" : "hover:bg-slate-50 active:bg-slate-100"}`}
              >
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${s.on_platform ? "bg-emerald-100" : "bg-slate-100"}`}>
                  {s.on_platform ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Store className="h-5 w-5 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                  {s.address && <p className="text-xs text-slate-400 truncate flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /> {s.address}</p>}
                </div>
                {s.on_platform ? (
                  <span data-testid={`invite-onplatform-${s.place_id}`} className="shrink-0 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">Già su KoupoNow</span>
                ) : (
                  <span className="shrink-0 text-xs font-bold text-[#BE123C] bg-[#FFE4E6] px-2 py-1 rounded-full">Scegli</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
